import { useState, FormEvent } from 'react';
import Input from './Input';
import Select from './Select';
import Button from './Button';
import { QueryType } from '../types';
import './QueryForm.css';

// Email regex pattern from requirements
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export interface QueryFormData {
  requestId?: string;
  productName?: string;
  email: string;
  queryType: QueryType;
  message: string;
}

export interface QueryFormProps {
  onSubmit: (data: QueryFormData) => Promise<void>;
  loading?: boolean;
  error?: string;
  successMessage?: string;
  initialData?: Partial<QueryFormData>;
}

const QUERY_TYPE_OPTIONS = [
  { value: 'Status Update', label: 'Status Update' },
  { value: 'Revision Request', label: 'Revision Request' },
  { value: 'General Inquiry', label: 'General Inquiry' },
];

function QueryForm({
  onSubmit,
  loading = false,
  error,
  successMessage,
  initialData,
}: QueryFormProps) {
  const [formData, setFormData] = useState<QueryFormData>({
    requestId: initialData?.requestId || '',
    productName: initialData?.productName || '',
    email: initialData?.email || '',
    queryType: initialData?.queryType || '' as QueryType,
    message: initialData?.message || '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Either Request ID or Product Name must be present
    const hasRequestId = formData.requestId?.trim();
    const hasProductName = formData.productName?.trim();

    if (!hasRequestId && !hasProductName) {
      newErrors.requestId = 'Request ID or Product Name is required';
      newErrors.productName = 'Request ID or Product Name is required';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!EMAIL_REGEX.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Query Type validation
    if (!formData.queryType) {
      newErrors.queryType = 'Query Type is required';
    }

    // Message validation (1-1000 characters)
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.length < 1) {
      newErrors.message = 'Message must be at least 1 character';
    } else if (formData.message.length > 1000) {
      newErrors.message = 'Message must be 1000 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: keyof QueryFormData) => (value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    // Clear the mutual error when user enters either field
    if (field === 'requestId' || field === 'productName') {
      if (errors.requestId || errors.productName) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.requestId;
          delete newErrors.productName;
          return newErrors;
        });
      }
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
    } catch {
      // Error is handled via props
    }
  };

  // Check if form is valid for submit button enablement
  const hasEitherIdentifier =
    formData.requestId?.trim() || formData.productName?.trim();
  const isQueryTypeSelected = formData.queryType === 'Status Update' || 
    formData.queryType === 'Revision Request' || 
    formData.queryType === 'General Inquiry';
  const isFormValid =
    hasEitherIdentifier &&
    formData.email.trim() !== '' &&
    EMAIL_REGEX.test(formData.email) &&
    isQueryTypeSelected &&
    formData.message.trim().length > 0 &&
    formData.message.length <= 1000;

  return (
    <form className="query-form" onSubmit={handleSubmit} noValidate>
      {error && (
        <div className="query-form__error" role="alert">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="query-form__success" role="status">
          {successMessage}
        </div>
      )}

      <div className="query-form__section">
        <h3 className="query-form__section-title">Query Information</h3>
        
        <Input
          label="Request ID"
          name="requestId"
          value={formData.requestId || ''}
          onChange={handleChange('requestId')}
          error={errors.requestId}
          placeholder="Enter Request ID (optional if Product Name provided)"
        />

        <Input
          label="Product Name"
          name="productName"
          value={formData.productName || ''}
          onChange={handleChange('productName')}
          error={errors.productName}
          placeholder="Enter Product Name (optional if Request ID provided)"
        />

        <Input
          label="Email Address"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange('email')}
          required
          error={errors.email}
          placeholder="Enter your email address"
        />

        <Select
          label="Query Type"
          name="queryType"
          options={QUERY_TYPE_OPTIONS}
          value={formData.queryType || ''}
          onChange={handleChange('queryType')}
          required
          error={errors.queryType}
        />

        <div className="query-form__textarea">
          <label htmlFor="message" className="query-form__label">
            Message
            <span className="query-form__field-group-optional">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={(e) => handleChange('message')(e.target.value)}
            maxLength={1000}
            className={`query-form__textarea-field ${
              errors.message ? 'query-form__textarea-field--error' : ''
            }`}
            placeholder="Enter your message (1-1000 characters)"
            rows={5}
            required
          />
          {errors.message && (
            <span className="query-form__field-error" role="alert">
              {errors.message}
            </span>
          )}
          <span className="query-form__char-count">
            {formData.message.length}/1000
          </span>
        </div>
      </div>

      <div className="query-form__actions">
        <Button
          type="submit"
          variant="primary"
          disabled={!isFormValid || loading}
          loading={loading}
        >
          Submit Query
        </Button>
      </div>
    </form>
  );
}

export default QueryForm;